import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { Chip } from "../primitives/Chip";
import type { makeGridStyles } from "./gridStyles";

// PE-27/PE-29 — the grid's structural handles. It used to carry four controls:
// two ＋ and two armed − that named their target ("− W1", "− D3").
//
// ⭐ PE-29 (Antenore, smoke 28/08) — the two − are gone, and not just because
// they were "bruttissimi". They were the odd gesture out: this grid already
// teaches hold-to-act on a cell (place/remove the focused exercise, PE-22 F),
// so a week or a day is now removed by holding ITS OWN header, with the
// platform confirm. That puts the action on the thing acted upon instead of on
// a button that has to say out loud which one it means — and it is why the
// labels here can shrink to "D ＋" / "W ＋" without losing anything: the ＋ no
// longer has a − beside it to be told apart from.
// ⛔ The hold is invisible until tried, so the row STATES it. A gesture nobody
// is told about is a gesture nobody has.
export function GridStructureRow({
  theme,
  styles,
  onAddDay,
  onAddWeek,
  canRemoveWeek,
  canRemoveDay,
}: {
  theme: UiTheme;
  styles: ReturnType<typeof makeGridStyles>;
  onAddDay?: () => void;
  onAddWeek?: () => void;
  /** A week can be held to remove it ⇒ the hint says so. */
  canRemoveWeek: boolean;
  /** A day can be held to remove it ⇒ the hint says so. */
  canRemoveDay: boolean;
}) {
  const { t } = useTranslation();
  // ⛔ The hint names only what is actually holdable. On a one-week program the
  // week cannot be removed, and telling someone to hold it would be a lie they
  // find out by holding.
  const hint =
    canRemoveDay && canRemoveWeek
      ? t("composer.holdToRemoveHint", {
          defaultValue: "Hold a day or a week to remove it",
        })
      : canRemoveDay
        ? t("composer.holdDayToRemoveHint", {
            defaultValue: "Hold a day to remove it",
          })
        : canRemoveWeek
          ? t("composer.holdWeekToRemoveHint", {
              defaultValue: "Hold a week to remove it",
            })
          : null;
  return (
    <View style={styles.gridStructureRow}>
      {onAddDay ? (
        <Chip
          theme={theme}
          shape="box"
          selected={false}
          onPress={onAddDay}
          label="D ＋"
          accessibilityLabel={t("composer.addDay", { defaultValue: "Add day" })}
        />
      ) : null}
      {onAddWeek ? (
        <Chip
          theme={theme}
          shape="box"
          selected={false}
          onPress={onAddWeek}
          label="W ＋"
          accessibilityLabel={t("composer.addWeek", { defaultValue: "Add week" })}
        />
      ) : null}
      {hint ? <Text style={styles.gridStructureHint}>{hint}</Text> : null}
    </View>
  );
}
